// src/redux/operatorSlice.ts  (WEB version)

import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  collection, doc, getDoc, getDocs,
  addDoc, setDoc, query, where, onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";           // your Firebase web init file
import type { AppDispatch, RootState } from "./store";

/* ═══════════ TYPES ═══════════ */

export type OperatorMode = "test" | "live";

export type OperatorKeys = {
  pk_live:             string | null;
  pk_test:             string | null;
  secret_key_live:     string | null;
  secret_key_test:     string | null;
  webhook_secret_live: string | null;
  webhook_secret_test: string | null;
};

export type Operator = {
  operatorId:            string;
  userId:                string;
  email:                 string;
  businessName:          string;
  businessType:          string;
  country:               string;
  currency:              string;
  website:               string | null;
  phone:                 string | null;
  status:                "active" | "pending" | "suspended";
  mode:                  OperatorMode;
  capabilities:          { ride_hailing: boolean; parcel_delivery: boolean; ride_sharing: boolean; scheduled_pickups: boolean };
  balance:               { available: number; pending: number; currency: string };
  allowParcelDeliveries: boolean;
  allowScheduledPickups: boolean;
  rideSharing:           boolean;
  parcelConfig:          { baseRate: number; perKmRate: number };
  webhookEndpoints:      any[];
  accountId?:            string;
  keysGenerated?:        boolean;
  joinDate:              string | null;
  updatedAt:             string | null;
  metadata?:             Record<string, any>;
};

type OperatorState = {
  operator:             Operator | null;
  operatorId:           string | null;
  mode:                 OperatorMode;
  operators:            Operator[];
  keys:                 OperatorKeys;
  unsubscribeOperator?: () => void;
  loading:              boolean;
  error:                string | null;
};

/* ═══════════ HELPERS ═══════════ */

const COLLECTION = "operators";

const toISO = (ts: any): string | null => {
  if (!ts) return null;
  if (ts?.toDate) return ts.toDate().toISOString();
  if (ts?._seconds) return new Date(ts._seconds * 1000).toISOString();
  return ts ?? null;
};

const parseOperator = (id: string, raw: any): Operator => ({
  operatorId:            id,
  userId:                raw.userId                ?? "",
  email:                 raw.email                 ?? "",
  businessName:          raw.businessName          ?? "",
  businessType:          raw.businessType          ?? "individual",
  country:               raw.country               ?? "",
  currency:              raw.currency              ?? "eur",
  website:               raw.website               ?? null,
  phone:                 raw.phone                 ?? null,
  status:                raw.status                ?? "active",
  mode:                  raw.mode                  ?? "test",
  capabilities:          raw.capabilities          ?? { ride_hailing: true, parcel_delivery: false, ride_sharing: false, scheduled_pickups: false },
  balance:               raw.balance               ?? { available: 0, pending: 0, currency: "eur" },
  allowParcelDeliveries: raw.allowParcelDeliveries  ?? false,
  allowScheduledPickups: raw.allowScheduledPickups  ?? false,
  rideSharing:           raw.rideSharing            ?? false,
  parcelConfig:          raw.parcelConfig            ?? { baseRate: 2.00, perKmRate: 0.50 },
  webhookEndpoints:      raw.webhookEndpoints        ?? [],
  accountId:             raw.accountId,
  keysGenerated:         raw.keysGenerated           ?? false,
  joinDate:              toISO(raw.joinDate),
  updatedAt:             toISO(raw.updatedAt),
  metadata:              raw.metadata               ?? {},
});

const emptyKeys = (): OperatorKeys => ({
  pk_live: null, pk_test: null,
  secret_key_live: null, secret_key_test: null,
  webhook_secret_live: null, webhook_secret_test: null,
});

/* ═══════════ THUNKS ═══════════ */

export const upsertOperator = createAsyncThunk(
  "operator/upsert",
  async (data: Partial<Operator> & { userId: string }, { rejectWithValue }) => {
    try {
      if (!data.userId) throw new Error("userId is required");
      const col = collection(db, COLLECTION);

      if (data.operatorId) {
        const { operatorId: _id, ...rest } = data;
        await setDoc(doc(col, _id), { ...rest, updatedAt: serverTimestamp() }, { merge: true });
        const snap = await getDoc(doc(col, _id));
        return snap.exists() ? parseOperator(snap.id, snap.data()) : null;
      }

      const docData = {
        ...data,
        status: "active", mode: "test", keysGenerated: false,
        allowParcelDeliveries: data.allowParcelDeliveries ?? false,
        allowScheduledPickups: data.allowScheduledPickups ?? false,
        rideSharing: data.rideSharing ?? false,
        parcelConfig: data.parcelConfig ?? { baseRate: 2.00, perKmRate: 0.50 },
        capabilities: { ride_hailing: true, parcel_delivery: false, ride_sharing: false, scheduled_pickups: false },
        balance: { available: 0, pending: 0, currency: data.currency ?? "eur" },
        webhookEndpoints: [],
        joinDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const newRef = await addDoc(col, docData);
      await setDoc(newRef, { operatorId: newRef.id }, { merge: true });
      const snap = await getDoc(newRef);
      return snap.exists() ? parseOperator(snap.id, snap.data()) : null;
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to upsert operator");
    }
  }
);

export const fetchOperator = createAsyncThunk<Operator, string>(
  "operator/fetch",
  async (operatorId, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, COLLECTION, operatorId));
      if (!snap.exists()) return rejectWithValue("Operator not found") as any;
      return parseOperator(snap.id, snap.data());
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchOperatorsByUserId = createAsyncThunk<Operator[], string>(
  "operator/fetchByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      const q    = query(collection(db, COLLECTION), where("userId", "==", userId));
      const snap = await getDocs(q);
      if (snap.empty) return rejectWithValue("No operators found") as any;
      return snap.docs
        .map(d => parseOperator(d.id, d.data()))
        .sort((a, b) => new Date(b.joinDate ?? 0).getTime() - new Date(a.joinDate ?? 0).getTime());
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/* ═══════════ REAL-TIME LISTENER ═══════════ */

export const listenToOperator =
  (operatorId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!operatorId) return;
    getState().operator.unsubscribeOperator?.();
    dispatch(setUnsubscribeOperator(undefined));

    const unsub = onSnapshot(
      doc(db, COLLECTION, operatorId),
      snap => {
        if (!snap.exists()) return;
        dispatch(setOperator(parseOperator(snap.id, snap.data())));
        dispatch(setOperatorId(operatorId));
        const existing = getState().operator.keys;
        const updated  = { ...existing, pk_live: snap.data()?.keys?.pk_live ?? existing.pk_live, pk_test: snap.data()?.keys?.pk_test ?? existing.pk_test };
        dispatch(setKeys(updated));
      },
      err => dispatch(setError(err.message))
    );
    dispatch(setUnsubscribeOperator(unsub));
    return unsub;
  };

export const cleanupOperatorListeners =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    getState().operator.unsubscribeOperator?.();
    dispatch(setUnsubscribeOperator(undefined));
  };

/* ═══════════ SLICE ═══════════ */

const initialState: OperatorState = {
  operator: null, operatorId: null, mode: "test",
  operators: [], keys: emptyKeys(), loading: false, error: null,
};

const operatorSlice = createSlice({
  name: "operator",
  initialState,
  reducers: {
    setOperator:   (state, action: PayloadAction<Operator>)             => { state.operator = action.payload; state.operatorId = action.payload.operatorId; },
    setOperatorId: (state, action: PayloadAction<string>)               => { state.operatorId = action.payload; },
    setOperators:  (state, action: PayloadAction<Operator[]>)           => { state.operators = action.payload; },
    setKeys:       (state, action: PayloadAction<Partial<OperatorKeys>>)=> { state.keys = { ...state.keys, ...action.payload }; },
    storeRegistrationKeys: (state, action: PayloadAction<{ operatorId: string; keys: any }>) => {
      const k = action.payload.keys;
      state.operatorId = action.payload.operatorId;
      state.keys = { pk_live: k.publishable_key_live ?? null, pk_test: k.publishable_key_test ?? null, secret_key_live: k.secret_key_live ?? null, secret_key_test: k.secret_key_test ?? null, webhook_secret_live: k.webhook_secret_live ?? null, webhook_secret_test: k.webhook_secret_test ?? null };
    },
    setMode:               (state, action: PayloadAction<OperatorMode>)          => { state.mode = action.payload; },
    setUnsubscribeOperator:(state, action: PayloadAction<(() => void) | undefined>) => { state.unsubscribeOperator = action.payload; },
    setError:              (state, action: PayloadAction<string | null>)           => { state.error = action.payload; },
    resetOperatorState:    (state) => { state.operator = null; state.operatorId = null; state.operators = []; state.keys = emptyKeys(); state.error = null; },
  },
  extraReducers: builder => {
    builder
      .addCase(upsertOperator.pending,   s => { s.loading = true;  s.error = null; })
      .addCase(upsertOperator.fulfilled, (s, a) => {
        s.loading = false;
        if (a.payload) {
          s.operator = a.payload; s.operatorId = a.payload.operatorId;
          const idx = s.operators.findIndex(o => o.operatorId === a.payload!.operatorId);
          if (idx >= 0) s.operators[idx] = a.payload; else s.operators.unshift(a.payload);
        }
      })
      .addCase(upsertOperator.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; });
    builder
      .addCase(fetchOperator.pending,   s => { s.loading = true;  s.error = null; })
      .addCase(fetchOperator.fulfilled, (s, a) => { s.loading = false; s.operator = a.payload; s.operatorId = a.payload.operatorId; })
      .addCase(fetchOperator.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; });
    builder
      .addCase(fetchOperatorsByUserId.pending,   s => { s.loading = true;  s.error = null; })
      .addCase(fetchOperatorsByUserId.fulfilled, (s, a) => {
        s.loading = false; s.operators = a.payload;
        if (a.payload.length > 0 && !s.operatorId) { s.operator = a.payload[0]; s.operatorId = a.payload[0].operatorId; }
      })
      .addCase(fetchOperatorsByUserId.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; });
  },
});

export const {
  setOperator, setOperatorId, setOperators, setKeys, storeRegistrationKeys,
  setMode, setUnsubscribeOperator, setError, resetOperatorState,
} = operatorSlice.actions;

export default operatorSlice.reducer;

export const selectOperator          = (s: RootState) => s.operator.operator;
export const selectOperatorId        = (s: RootState) => s.operator.operatorId;
export const selectOperators         = (s: RootState) => s.operator.operators;
export const selectMode              = (s: RootState) => s.operator.mode;
export const selectIsLive            = (s: RootState) => s.operator.mode === "live";
export const selectKeys              = (s: RootState) => s.operator.keys;
export const selectOperatorLoading   = (s: RootState) => s.operator.loading;
export const selectOperatorError     = (s: RootState) => s.operator.error;
export const selectCanDeliverParcels = (s: RootState) => s.operator.operator?.allowParcelDeliveries ?? false;
export const selectCanShareRides     = (s: RootState) => s.operator.operator?.rideSharing ?? false;
export const selectSecretKey         = (s: RootState): string | null => s.operator.mode === "live" ? s.operator.keys.secret_key_live : s.operator.keys.secret_key_test;
export const selectPublishableKey    = (s: RootState): string | null => s.operator.mode === "live" ? s.operator.keys.pk_live : s.operator.keys.pk_test;
export const selectWebhookSecret     = (s: RootState): string | null => s.operator.mode === "live" ? s.operator.keys.webhook_secret_live : s.operator.keys.webhook_secret_test;